<template lang="pug">
  v-card.pa-4
    v-row.mb-sm-4(no-gutters)
      v-col.px-0.pt-3(cols="3", sm="2")
        PostAvatar(:viewContext="viewContext", :creator="post.creator")
      v-col(cols="9", sm="10")
        v-row.mb-4(no-gutters)
          v-col(cols="10", sm="11")
            PostHeader.mt-2(:post="post", :viewContext="viewContext")
          v-col.d-flex.justify-end(cols="2", sm="1")
            PopupMenu(:items="permittedMenuItems", v-if="permittedMenuItems.length")
        RenderedWysiwygContent(
          :html="post.text",
          v-if="!isXsScreenSize && !isEdit",
          custom-class="mt-4"
          style="overflow-x:auto;"
          :attachments="post.attachments"
        )
      v-col(cols="12")
        RenderedWysiwygContent(
          :html="post.text",
          v-if="isXsScreenSize && !isEdit",
          custom-class="mt-4"
          style="overflow-x:auto;"
          :attachments="post.attachments"
        )
      v-col(cols="12", v-if="post.event_media_preview?.length > 0 & !isEdit")
        PostGalleryPreview.pl-sm-8.mt-4(:post="post")

    PostForm(
      :value="post",
      v-if="isEdit",
      :update-post="updatePost",
      @postUpdated="onPostUpdated",
      :viewContext="viewContext",
      :isEdit="isEdit",
      :inline="false"
    )
    v-row(no-gutters, justify="end")
      v-col.d-flex.align-center(cols="8", sm="3", offset-sm="2")
        ReactionIcon(
          v-if="highlightsCount > 0",
          style="margin-right: 8px;",
          :count="highlightsCount",
          :icon="mdiStar",
          :size="20",
          :tooltip="highlightsCountTooltip"
        )
        ReactionCountFull.my-1.my-1(
          :likes-count="likesCount",
          :ideas-count="ideasCount",
          :cheers-count="cheersCount",
          :member-reactions="memberReactions"
          @reaction-clicked="handleReactionClick"
        )
      v-col.d-flex.justify-end(cols="4", sm="7")
        ReactionButton(
          :disabled="!canCreateReaction",
          :variant="isXsScreenSize ? 'icon' : 'text'",
          :selected-reaction="userReaction",
          @delete-reaction="handleDeleteReaction",
          @create-reaction="handleCreateReaction",
          @update-reaction="handleUpdateReaction"
        )
        CommentButton.ml-4(
          :disabled="!canCreateComment",
          @click="showCommentForm = !showCommentForm",
          v-if="!isXsScreenSize"
        )
      v-col.d-flex.justify-end(cols="12", v-if="isXsScreenSize")
        CommentButton(@click="showCommentForm = !showCommentForm" :disabled="!canCreateComment")
    CommentSection(
      :initial-comments="post.comments",
      :comment-form-open="showCommentForm"
      @changeCommentFormVisibility="showCommentForm = $event"
      :can-create-comment="canCreateComment"
      :viewContext="viewContext"
      :post-id="post.id"
    )
</template>

<script lang="ts">
import {
  ref,
  computed,
  defineComponent,
  PropType,
  Ref,
  ComputedRef,
  provide,
  inject,
} from "vue"
import { Post as IPost, Emoji, Post } from "suus-api"
import { IAvatar } from "interfaces/avatar.interface"
import { MenuItem } from "interfaces/menuItem.interface"
import PostForm from "features/posts/form/PostForm.vue"
import PopupMenu from "../../app/ui-primitives/menus/popup-menu/PopupMenu.vue"
import RelativeTime from "../UI/RelativeTime.vue"
import TimeDelta from "../UI/TimeDelta.vue"
import { mdiPencilOutline, mdiDeleteOutline, mdiStar } from "@mdi/js"
import TextHTML from "components/UI/TextHTML.vue"
import { ViewContext } from "concerns/useContextName"
import Avatar from "components/UI/Avatar.vue"
import Subtitle from "components/UI/Subtitle.vue"
import useReaction from "concerns/useReaction"
import CommentButton from "components/Comment/CommentButton.vue"
import ReactionButton from "components/ReactionButton/index.vue"
import CommentForm from "components/Comment/CommentForm.vue"
import { MemberReaction } from "components/ReactionCount/reaction-count.types"
import PostAvatar from "components/Post/PostAvatar.vue"
import PostHeader from "components/Post/PostHeader.vue"
import AttachmentListItemGroup from "components/AttachmentListItemGroup.vue"
import { UpdatePostFunction } from "features/posts/form/post-form.types"
import PostGalleryPreview from "features/posts/gallery/PostGalleryPreview.vue"
import { PostLightboxService } from "features/posts/gallery/lightbox/post-lightbox.service"
import { LightboxService } from "features/gallery/lightbox/lightbox.service"
import CommentSection from "components/Comment/CommentSection.vue"
import ReactionCountFull from "components/ReactionCount/ReactionCountFull.vue"
import RenderedWysiwygContent from "components/Wysiwyg/RenderedWysiwygContent.vue"
import ReactionIcon from "components/ReactionIcon/ReactionIcon.vue"
import { useConfirmationService } from "features/common/confirmation-dialog/confirmation.service"

export default defineComponent({
  name: "Post",
  components: {
    ReactionIcon,
    ReactionCountFull,
    RenderedWysiwygContent,
    CommentSection,
    PostGalleryPreview,
    PostHeader,
    PostAvatar,
    AttachmentListItemGroup,
    PostForm,
    TimeDelta,
    TextHTML,
    RelativeTime,
    PopupMenu,
    Avatar,
    Subtitle,
    CommentButton,
    ReactionButton,
    CommentForm,
  },
  props: {
    post: { type: Object as PropType<IPost>, required: true },
    viewContext: { type: Object as PropType<ViewContext> },
    updatePost: {
      type: Function as PropType<UpdatePostFunction>,
    },
  },
  setup(props, context) {
    provide("viewContext", props.viewContext)

    const lightboxService: LightboxService | undefined = inject(
      "lightboxService",
      undefined
    )
    if (lightboxService) {
      const postLightboxService = new PostLightboxService(
        lightboxService,
        props.post,
        props.viewContext
      )

      provide("PostLightboxService", postLightboxService)
    }

    const showCommentForm = ref(false)

    const initialReactionCounts = computed(() => {
      return {
        likesCount: props.post.likes_count,
        ideasCount: props.post.ideas_count,
        cheersCount: props.post.cheers_count,
      }
    })

    const initialUserReaction = computed(() => props.post.own_reaction?.emoji)
    const {
      updateReaction,
      createReaction,
      deleteReaction,
      likesCount,
      ideasCount,
      cheersCount,
      reactions,
      userReaction,
    } = useReaction(
      { id: props.post.id, type: "Post" },
      initialReactionCounts,
      initialUserReaction
    )

    const memberReactions: ComputedRef<MemberReaction[]> = computed(() => {
      return reactions.value.map((reaction) =>
        MemberReaction.fromReaction(reaction, props.viewContext)
      )
    })

    async function handleDeleteReaction(val: Emoji): Promise<void> {
      await deleteReaction(props.post.id.toString(), val)
    }

    async function handleCreateReaction(val: Emoji): Promise<void> {
      await createReaction(props.post.id.toString(), val)
    }

    async function handleUpdateReaction({ prevReaction, newReaction }) {
      await updateReaction(props.post.id.toString(), newReaction, prevReaction)
    }

    async function handleReactionClick(reaction: Emoji) {
      const postId = props.post.id.toString()
      const currentReaction = userReaction.value

      if (currentReaction === reaction) {
        await deleteReaction(postId, reaction)
      } else if (currentReaction) {
        await updateReaction(postId, reaction, currentReaction)
      } else {
        await createReaction(postId, reaction)
      }
    }

    const canCreateReaction = computed(
      () => props.post.links.reactions?.create_url
    )
    const canCreateComment = computed(
      () => !!props.post.links.comments?.create_url
    )

    const isEdit = ref(false)
    const isDialog = ref(false)

    const avatarData: Ref<IAvatar> = computed(() => {
      return {
        name: props.post?.creator.internal_name_short,
        imageUrl: props.post?.creator.avatar_url,
        profileUrl: props.post?.creator.url,
        clients: props.post?.creator.clients,
      }
    })

    function onPostUpdated(post: Post): void {
      context.emit("postUpdated", post)
      isEdit.value = false
    }

    const confirmationService = useConfirmationService()
    function deletePost(): void {
      confirmationService.open({
        title: "Antwort löschen",
        text: "Möchtest du dieser Antwort wirklich löschen?",
        confirmText: "Löschen",
        cancelText: "Abbrechen",
        onConfirm: async () => {
          context.emit("delete-post", props.post.id)
        },
      })
    }

    const menuItems: Ref<MenuItem[]> = computed(() => {
      return [
        {
          title: "Bearbeiten",
          key: "edit",
          clickHandler: () => (isEdit.value = true),
          icon: mdiPencilOutline,
          permitted: !!props.post.links.update_url,
        },
        {
          title: "Löschen",
          key: "delete",
          clickHandler: deletePost,
          icon: mdiDeleteOutline,
          permitted: !!props.post.links.delete_url,
        },
      ]
    })

    const permittedMenuItems = computed(() =>
      menuItems.value.filter((item) => item.permitted)
    )

    const postAttachments = computed(() => props.post.attachments)

    const highlightsCount = computed(() => {
      return props.post.highlights_count
    })

    const highlightsCountTooltip = computed(() => {
      if (highlightsCount.value === 1) {
        return `Ein Foto wurde als Highlight markiert.`
      }
      return `${highlightsCount.value} Fotos wurden als Highlight markiert.`
    })

    return {
      isEdit,
      isDialog,
      onPostUpdated,
      deletePost,
      avatarData,
      permittedMenuItems,
      showCommentForm,
      likesCount,
      ideasCount,
      cheersCount,
      userReaction,
      canCreateReaction,
      canCreateComment,
      handleDeleteReaction,
      handleCreateReaction,
      handleUpdateReaction,
      handleReactionClick,
      memberReactions,
      postAttachments,
      mdiStar,
      highlightsCount,
      highlightsCountTooltip,
    }
  },
  computed: {
    isXsScreenSize(): boolean {
      return this.$vuetify.breakpoint.name === "xs"
    },
  },
})
</script>
